import { create } from 'zustand';

import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/shared/constants/pagination';

import type {
  PatientSortKey,
  PatientStatusFilter,
  SortDirection,
} from '../types/patient.types';

export interface PatientListFilters {
  // Free-text — case-insensitive partial match on name / phone / regNo (PRD §3.2).
  search: string;
  status: PatientStatusFilter;
}

export interface PatientListSort {
  key: PatientSortKey;
  dir: SortDirection;
}

// PRD §2.2 #6: rows-per-page dropdown options — shared 20/30/50 set (SHCT-715).
export const PATIENT_PER_PAGE_OPTIONS = PAGE_SIZE_OPTIONS;

interface PatientListState {
  filters: PatientListFilters;
  sort: PatientListSort;
  // PRD §3.3 tri-state: false when sort is the default (registeredAt desc).
  // The column-header indicator uses this to render ↕ on non-default columns
  // and ↓ on registeredAt at rest.
  userSorted: boolean;
  page: number;
  perPage: number;
  setFilters: (partial: Partial<PatientListFilters>) => void;
  // PRD §3.3 3-click cycle: asc → desc → cleared (back to default).
  toggleSort: (key: PatientSortKey) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  reset: () => void;
}

const INITIAL_FILTERS: PatientListFilters = { search: '', status: 'all' };
// PRD §4.1: Registration Date is sortable Desc by default (most recently registered first).
const INITIAL_SORT: PatientListSort = { key: 'registeredAt', dir: 'desc' };

export const usePatientListStore = create<PatientListState>((set) => ({
  filters: INITIAL_FILTERS,
  sort: INITIAL_SORT,
  userSorted: false,
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  // Filter / sort / perPage changes reset pagination to page 1 (prototype behavior).
  setFilters: (partial) =>
    set((s) => ({ filters: { ...s.filters, ...partial }, page: 1 })),
  toggleSort: (key) =>
    set((s) => {
      if (s.userSorted && s.sort.key === key) {
        // Same column, user-set: asc → desc → clear-back-to-default.
        if (s.sort.dir === 'asc') {
          return { sort: { key, dir: 'desc' }, userSorted: true, page: 1 };
        }
        return { sort: INITIAL_SORT, userSorted: false, page: 1 };
      }
      // Different column (or at default) → enter user-asc state.
      return { sort: { key, dir: 'asc' }, userSorted: true, page: 1 };
    }),
  setPage: (page) => set({ page }),
  setPerPage: (perPage) => set({ perPage, page: 1 }),
  reset: () =>
    set({
      filters: INITIAL_FILTERS,
      sort: INITIAL_SORT,
      userSorted: false,
      page: 1,
      perPage: DEFAULT_PAGE_SIZE,
    }),
}));
